import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Paperclip, RefreshCw, Download, Sparkles, Check, X, FileText, Building2, MapPin, Phone, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MARKET, ORDER_TYPE, PRIORITY, NEED, SCOPE, CUSTOMER_TYPE, INQ_STATUS, timeAgo, fmtDateTime, todayISO } from "@/lib/format";
import { InquiryStatus, Chip } from "@/components/drift/StatusBadge";
import { PageHeader, Panel, EmptyState, LoadingRows, Field, Avatar, Segmented } from "@/components/drift/common";
import { usePageCrumb } from "@/components/drift/DriftLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const TABS = [["ny", "Nye"], ["under_behandling", "Under arbeid"], ["konvertert", "Konverterte"], ["avvist", "Avviste"], ["alle", "Alle"]];
const NONE = "__none__";

function InquiryList({ items, counts, status, setStatus, activeId, q, setQ, loading }) {
  return (
    <div className="shadow-panel flex h-full flex-col rounded-xl bg-card" data-testid="inquiries-list">
      <div className="border-b border-border/70 p-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk namn, firma, referanse …" className="h-9 rounded-lg bg-background" data-testid="inquiries-search-input" aria-label="Søk i henvendingar" />
        <Segmented size="sm" className="mt-3 w-full" options={TABS.map(([k, l]) => [k, l, k !== "alle" && counts?.[k] ? counts[k] : undefined])} value={status} onChange={setStatus} testid="inquiries-tab" />
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {loading && <div className="p-3"><LoadingRows n={5} /></div>}
        {!loading && items.length === 0 && <div className="p-3"><EmptyState title="Ingen henvendingar" text="Nye førespurnader frå nettsida dukkar opp her." /></div>}
        <ul className="divide-y divide-border/60">
          {items.map((i) => (
            <li key={i.id}>
              <Link to={`/drift/henvendingar/${i.id}${status !== "ny" ? `?status=${status}` : ""}`} className={cn("relative block px-4 py-3 transition-colors duration-150 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring", activeId === i.id && "bg-accent/60 before:absolute before:bottom-3 before:left-0 before:top-3 before:w-0.5 before:rounded-full before:bg-brand")} data-testid="inquiry-list-item">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-[13.5px] font-medium">{i.company || i.name}</span>
                  <span className="shrink-0 text-[11.5px] text-muted-foreground">{timeAgo(i.created_at)}</span>
                </div>
                <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{i.message}</p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <InquiryStatus status={i.status} size="sm" />
                  {i.ai_status === "done" && i.ai && (<>
                    <Chip>{ORDER_TYPE[i.ai.oppdragstype]}</Chip>
                    {(i.ai.hastegrad === "hoeg" || i.ai.hastegrad === "akutt") && <Chip className={i.ai.hastegrad === "akutt" ? "tone-danger" : "tone-warn"}>{PRIORITY[i.ai.hastegrad]}</Chip>}
                  </>)}
                  {i.ai_status === "pending" && <Chip className="text-muted-foreground">Les gjennom …</Chip>}
                  {i.attachment_count > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Paperclip className="h-3 w-3" />{i.attachment_count}</span>}
                  <span className="tabular ml-auto font-mono text-[11px] text-muted-foreground">{i.ref}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SuggestRow({ label, children }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 py-2 text-[13.5px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0">{children}</span>
    </div>
  );
}

function ConvertDialog({ inq, open, onOpenChange, onDone }) {
  const ai = inq?.ai || {};
  const match = inq?.customer_match;
  const [customers, setCustomers] = useState([]);
  const [montorar, setMontorar] = useState([]);
  const [mode, setMode] = useState(match ? "existing" : "new");
  const [customerId, setCustomerId] = useState(match?.id || "");
  const [locations, setLocations] = useState(match?.locations || []);
  const [locMode, setLocMode] = useState(match?.locations?.length ? "existing" : "new");
  const [locationId, setLocationId] = useState(match?.locations?.[0]?.id || "");
  const [nc, setNc] = useState({ name: inq?.company || ai.kunde_namn || inq?.name || "", type: ai.kunde_type || (inq?.company ? "naering" : "privat"), contact_name: inq?.name || "", email: inq?.email || "", phone: inq?.phone || "", address: inq?.address || "" });
  const [nl, setNl] = useState({ name: ai.lokasjon || inq?.address || "", address: inq?.address || ai.lokasjon || "", place: "" });
  const [o, setO] = useState({ title: ai.tittel || "", type: ai.oppdragstype || "service", market: ai.marknad || inq?.market || "service_bustad", priority: ai.hastegrad || "normal", description: ai.samandrag || inq?.message || "", assigned_to: "", scheduled_date: "", scheduled_time: "", estimated_hours: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/drift/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/drift/users?role=montor").then((r) => setMontorar(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (mode !== "existing" || !customerId) return;
    api.get(`/drift/customers/${customerId}`).then((r) => { setLocations(r.data.locations || []); if (r.data.locations?.length) { setLocMode("existing"); setLocationId(r.data.locations[0].id); } else { setLocMode("new"); } }).catch(() => {});
  }, [mode, customerId]);

  const submit = async () => {
    if (mode === "existing" && !customerId) { toast.error("Vel ein kunde"); return; }
    if (mode === "new" && !nc.name.trim()) { toast.error("Kundenamn manglar"); return; }
    if (!o.title.trim()) { toast.error("Oppdraget treng ein tittel"); return; }
    setBusy(true);
    try {
      const payload = {
        customer_id: mode === "existing" ? customerId : null,
        new_customer: mode === "new" ? nc : null,
        location_id: mode === "existing" && locMode === "existing" ? locationId : null,
        new_location: !(mode === "existing" && locMode === "existing") ? nl : null,
        order: { ...o, assigned_to: o.assigned_to || null, scheduled_date: o.scheduled_date || null, scheduled_time: o.scheduled_time || null, estimated_hours: o.estimated_hours ? Number(o.estimated_hours) : null },
      };
      const r = await api.post(`/drift/inquiries/${inq.id}/convert`, payload);
      toast.success(`Oppdrag ${r.data.order.ref} oppretta`);
      onOpenChange(false);
      onDone(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-2xl" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Opprett oppdrag frå henvending</DialogTitle>
          <DialogDescription>Felta er fylte ut frå forslaget. Kontroller og juster før du opprettar.</DialogDescription>
        </DialogHeader>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">1. Kunde</h3>
            <div className="flex gap-0.5 rounded-lg bg-secondary p-0.5 text-xs font-medium">
              <button type="button" onClick={() => setMode("existing")} className={cn("rounded-md px-2.5 py-1 transition-colors", mode === "existing" ? "bg-card shadow-[0_1px_0_hsl(var(--border))]" : "text-muted-foreground hover:text-foreground")} data-testid="convert-customer-mode-existing">Eksisterande</button>
              <button type="button" onClick={() => setMode("new")} className={cn("rounded-md px-2.5 py-1 transition-colors", mode === "new" ? "bg-card shadow-[0_1px_0_hsl(var(--border))]" : "text-muted-foreground hover:text-foreground")} data-testid="convert-customer-mode-new">Ny kunde</button>
            </div>
          </div>
          {mode === "existing" ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Kunde" className="sm:col-span-2">
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger data-testid="convert-customer-select"><SelectValue placeholder="Vel kunde" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-muted-foreground">· {CUSTOMER_TYPE[c.type]}</span></SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Anlegg" className="sm:col-span-2">
                <Select value={locMode === "existing" ? locationId : NONE} onValueChange={(v) => { if (v === NONE) { setLocMode("new"); } else { setLocMode("existing"); setLocationId(v); } }}>
                  <SelectTrigger data-testid="convert-location-select"><SelectValue placeholder="Vel anlegg" /></SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} – {l.address}</SelectItem>)}
                    <SelectItem value={NONE}>+ Nytt anlegg</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {locMode === "new" && (<>
                <Field label="Namn på anlegg"><Input value={nl.name} onChange={(e) => setNl({ ...nl, name: e.target.value })} data-testid="convert-newloc-name-input" /></Field>
                <Field label="Adresse"><Input value={nl.address} onChange={(e) => setNl({ ...nl, address: e.target.value })} data-testid="convert-newloc-address-input" /></Field>
              </>)}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Namn"><Input value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} data-testid="convert-newcust-name-input" /></Field>
              <Field label="Type">
                <Select value={nc.type} onValueChange={(v) => setNc({ ...nc, type: v })}><SelectTrigger data-testid="convert-newcust-type-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CUSTOMER_TYPE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
              </Field>
              <Field label="Kontaktperson"><Input value={nc.contact_name} onChange={(e) => setNc({ ...nc, contact_name: e.target.value })} /></Field>
              <Field label="Telefon"><Input value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} /></Field>
              <Field label="E-post"><Input value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} /></Field>
              <Field label="Adresse"><Input value={nc.address} onChange={(e) => setNc({ ...nc, address: e.target.value })} /></Field>
              <Field label="Anlegg / lokasjon" className="sm:col-span-2" hint="Anlegget vert oppretta under kunden, slik at historikk kan byggjast opp."><Input value={nl.name} onChange={(e) => setNl({ ...nl, name: e.target.value, address: nl.address || e.target.value })} placeholder="T.d. Næringsbygg Husnes" data-testid="convert-newloc2-name-input" /></Field>
            </div>
          )}
        </section>

        <section className="mt-4 space-y-3">
          <h3 className="text-sm font-medium">2. Oppdrag</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tittel" className="sm:col-span-2"><Input value={o.title} onChange={(e) => setO({ ...o, title: e.target.value })} data-testid="convert-order-title-input" /></Field>
            <Field label="Beskriving" className="sm:col-span-2"><Textarea rows={3} value={o.description} onChange={(e) => setO({ ...o, description: e.target.value })} data-testid="convert-order-description-input" /></Field>
            <Field label="Type"><Select value={o.type} onValueChange={(v) => setO({ ...o, type: v })}><SelectTrigger data-testid="convert-order-type-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ORDER_TYPE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Marknad"><Select value={o.market} onValueChange={(v) => setO({ ...o, market: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(MARKET).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Prioritet"><Select value={o.priority} onValueChange={(v) => setO({ ...o, priority: v })}><SelectTrigger data-testid="convert-order-priority-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Estimerte timar"><Input type="number" step="0.5" value={o.estimated_hours} onChange={(e) => setO({ ...o, estimated_hours: e.target.value })} /></Field>
            <Field label="Montør (valfritt)">
              <Select value={o.assigned_to || NONE} onValueChange={(v) => setO({ ...o, assigned_to: v === NONE ? "" : v })}>
                <SelectTrigger data-testid="convert-order-montor-select"><SelectValue placeholder="Planlegg seinare" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Planlegg seinare</SelectItem>{montorar.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Dato"><Input type="date" min={todayISO()} value={o.scheduled_date} onChange={(e) => setO({ ...o, scheduled_date: e.target.value })} data-testid="convert-order-date-input" /></Field>
              <Field label="Tid"><Input type="time" value={o.scheduled_time} onChange={(e) => setO({ ...o, scheduled_time: e.target.value })} /></Field>
            </div>
          </div>
        </section>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={submit} disabled={busy} className="bg-brand text-brand-foreground hover:bg-brand-dark" data-testid="convert-submit-button">{busy ? "Opprettar …" : "Opprett oppdrag"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InquiryDetail({ id, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inq, setInq] = useState(null);
  const [err, setErr] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const pollRef = useRef(null);
  usePageCrumb(inq?.ref);

  const load = useCallback(() => api.get(`/drift/inquiries/${id}`).then((r) => { setInq(r.data); setNotes(r.data.notes || ""); setErr(null); }).catch((e) => setErr(e)), [id]);

  useEffect(() => { setInq(null); load(); }, [load]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (inq?.ai_status === "pending") pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
  }, [inq?.ai_status, load]);

  const patch = async (data, msg) => {
    setBusy(true);
    try {
      const r = await api.patch(`/drift/inquiries/${id}`, data);
      setInq(r.data);
      onChanged();
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const reclassify = async () => {
    setInq((p) => ({ ...p, ai_status: "pending" }));
    try {
      const r = await api.post(`/drift/inquiries/${id}/classify`);
      setInq(r.data);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };

  const makeQuote = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/drift/inquiries/${id}/quote`, { customer_id: inq.customer_match?.id || null });
      toast.success(`Tilbod ${r.data.ref} oppretta`);
      load();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (err) return <EmptyState title="Fann ikkje henvendinga" action={<Button variant="outline" asChild><Link to="/drift/henvendingar">Tilbake</Link></Button>} />;
  if (!inq) return <LoadingRows n={8} />;

  const ai = inq.ai;
  const canConvert = inq.status !== "konvertert" && inq.status !== "avvist";

  return (
    <div data-testid="inquiry-detail">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/drift/henvendingar" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground lg:hidden"><ArrowLeft className="h-3.5 w-3.5" /> Alle henvendingar</Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground" data-testid="inquiry-detail-ref">{inq.ref}</span>
            <InquiryStatus status={inq.status} />
            <span className="text-xs text-muted-foreground">{fmtDateTime(inq.created_at)} · via {inq.source}</span>
          </div>
          <h2 className="mt-1 font-display text-2xl font-medium tracking-[-0.02em]" data-testid="inquiry-detail-title">{ai?.tittel || inq.company || inq.name}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {canConvert && <Button onClick={() => setConvertOpen(true)} className="bg-brand text-brand-foreground hover:bg-brand-dark" data-testid="inquiry-convert-button">Opprett oppdrag <ArrowRight className="ml-1.5 h-4 w-4" /></Button>}
          {canConvert && !inq.converted_quote_id && <Button variant="outline" onClick={makeQuote} disabled={busy} data-testid="inquiry-create-quote-button"><FileText className="mr-1.5 h-4 w-4" /> Opprett tilbod</Button>}
          {inq.status === "ny" && <Button variant="outline" onClick={() => patch({ status: "under_behandling", assigned_to: user.id }, "Sett under arbeid")} disabled={busy} data-testid="inquiry-take-button">Ta saka</Button>}
          {canConvert && <Button variant="ghost" onClick={() => patch({ status: "avvist" }, "Henvendinga er avvist")} disabled={busy} data-testid="inquiry-reject-button"><X className="mr-1.5 h-4 w-4" /> Avvis</Button>}
          {inq.status === "avvist" && <Button variant="outline" onClick={() => patch({ status: "ny" }, "Opna att")} disabled={busy}>Opne att</Button>}
        </div>
      </div>

      {inq.converted_order && (
        <Link to={`/drift/oppdrag/${inq.converted_order.id}`} className="tone-ok mb-4 flex items-center justify-between rounded-xl px-4 py-3 text-sm ring-1 transition-opacity hover:opacity-90" data-testid="inquiry-converted-order-link">
          <span className="inline-flex items-center gap-2"><Check className="h-4 w-4" /> Konvertert til oppdrag <span className="font-mono">{inq.converted_order.ref}</span> – {inq.converted_order.title}</span>
          <ArrowRight className="h-4 w-4" />
        </Link>
      )}
      {inq.converted_quote && (
        <Link to="/drift/tilbod" className="tone-info mb-4 flex items-center justify-between rounded-xl px-4 py-3 text-sm ring-1 transition-opacity hover:opacity-90" data-testid="inquiry-converted-quote-link">
          <span className="inline-flex items-center gap-2"><FileText className="h-4 w-4" /> Tilbod <span className="font-mono">{inq.converted_quote.ref}</span> – {inq.converted_quote.title}</span>
          <ArrowRight className="h-4 w-4" />
        </Link>
      )}

      <div className="grid gap-5 xl:grid-cols-5">
        <div className="space-y-5 xl:col-span-3">
          <Panel title="Melding" testid="inquiry-message-panel">
            <p className="whitespace-pre-wrap text-[14px] leading-relaxed">{inq.message}</p>
            <div className="mt-5 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <span className="inline-flex items-center gap-2"><Avatar name={inq.name} size="sm" /> {inq.name}{inq.company ? <span className="text-muted-foreground">· {inq.company}</span> : null}</span>
              {inq.email && <a href={`mailto:${inq.email}`} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"><Mail className="h-3.5 w-3.5" /> {inq.email}</a>}
              {inq.phone && <a href={`tel:${inq.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 font-mono text-muted-foreground hover:text-foreground"><Phone className="h-3.5 w-3.5" /> {inq.phone}</a>}
              {inq.address && <span className="inline-flex items-center gap-2 text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {inq.address}</span>}
              {inq.timing && <span className="inline-flex items-center gap-2 text-muted-foreground" data-testid="inquiry-timing"><Clock className="h-3.5 w-3.5" /> Ønskt tidspunkt: {inq.timing}</span>}
              <span className="inline-flex items-center gap-2 text-muted-foreground"><Building2 className="h-3.5 w-3.5" /> Valt: {MARKET[inq.market]}</span>
            </div>
          </Panel>

          <Panel title={`Vedlegg · ${inq.attachment_files?.length || 0}`} bodyClass="p-0" testid="inquiry-attachments-panel">
            {!inq.attachment_files?.length ? <p className="p-4 text-sm text-muted-foreground">Ingen vedlegg.</p> : (
              <ul className="divide-y divide-border/60">
                {inq.attachment_files.map((f) => (
                  <li key={f.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    {f.content_type?.startsWith("image/") ? <img src={fileUrl(f.id)} alt={f.filename} className="h-10 w-10 rounded-lg object-cover" /> : <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary"><FileText className="h-4 w-4" /></span>}
                    <span className="min-w-0 flex-1 truncate">{f.filename}</span>
                    <span className="font-mono text-xs text-muted-foreground">{Math.round((f.size || 0) / 1024)} kB</span>
                    <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" title="Opne" data-testid="inquiry-attachment-open-link"><Download className="h-4 w-4" /></a>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Interne notat">
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (inq.notes || "") && patch({ notes }, "Notat lagra")} placeholder="Notat for kontoret …" data-testid="inquiry-notes-input" />
          </Panel>
        </div>

        <div className="space-y-5 xl:col-span-2">
          <Panel
            title={<span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} /> Forslag</span>}
            action={<button onClick={reclassify} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="inquiry-reclassify-button"><RefreshCw className={cn("h-3.5 w-3.5", inq.ai_status === "pending" && "animate-spin")} /> Les på nytt</button>}
            testid="inquiry-ai-panel"
          >
            {inq.ai_status === "pending" && (
              <div className="space-y-3" data-testid="inquiry-ai-pending">
                <p className="font-mono text-xs text-muted-foreground">Les gjennom henvendinga og strukturerer …</p>
                {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-5 w-full rounded-md bg-secondary" />)}
              </div>
            )}
            {inq.ai_status === "failed" && (
              <div className="text-sm" data-testid="inquiry-ai-failed">
                <p className="text-warn">Klarte ikkje lese henvendinga automatisk.</p>
                <p className="mt-1 text-xs text-muted-foreground">{inq.ai_error}</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={reclassify}>Prøv igjen</Button>
              </div>
            )}
            {inq.ai_status === "done" && ai && (
              <div className="divide-y divide-border" data-testid="inquiry-ai-done">
                <SuggestRow label="Kunde">{ai.kunde_namn || "–"} {ai.kunde_type && <span className="text-muted-foreground">· {CUSTOMER_TYPE[ai.kunde_type]}</span>}</SuggestRow>
                {inq.customer_match && <SuggestRow label="Treff i register"><Link to={`/drift/kundar/${inq.customer_match.id}`} className="text-ok underline underline-offset-4" data-testid="inquiry-customer-match-link">{inq.customer_match.name}</Link> <span className="text-muted-foreground">· {inq.customer_match.locations?.length || 0} anlegg</span></SuggestRow>}
                <SuggestRow label="Lokasjon">{ai.lokasjon || "–"}</SuggestRow>
                <SuggestRow label="Marknad">{MARKET[ai.marknad]}</SuggestRow>
                <SuggestRow label="Fag"><span className="flex flex-wrap gap-1">{(ai.fag || []).map((f) => <Chip key={f}>{f}</Chip>)}</span></SuggestRow>
                <SuggestRow label="Oppdrag">{ORDER_TYPE[ai.oppdragstype]}{ai.estimert_omfang && <span className="text-muted-foreground"> · {SCOPE[ai.estimert_omfang]} omfang</span>}</SuggestRow>
                <SuggestRow label="Antatt behov">{NEED[ai.antatt_behov] || ai.antatt_behov}</SuggestRow>
                <SuggestRow label="Hastegrad"><span className={cn(ai.hastegrad === "akutt" && "text-danger", ai.hastegrad === "hoeg" && "text-warn")}>{PRIORITY[ai.hastegrad]}</span></SuggestRow>
                {ai.vedlegg_tolking && <SuggestRow label="Vedlegg">{ai.vedlegg_tolking}</SuggestRow>}
                <SuggestRow label="Ansvarleg">{inq.suggested_user ? <span className="inline-flex items-center gap-2"><Avatar name={inq.suggested_user.name} size="sm" /> {inq.suggested_user.name}</span> : ai.foreslaatt_ansvarleg || "–"}</SuggestRow>
                <div className="py-3">
                  <p className="text-xs text-muted-foreground">Samandrag</p>
                  <p className="mt-1 text-sm leading-relaxed">{ai.samandrag}</p>
                </div>
                <div className="py-3">
                  <p className="text-xs text-muted-foreground">Neste steg</p>
                  <ul className="mt-1 space-y-1">{(ai.neste_steg || []).map((s, i) => <li key={i} className="flex gap-2 text-sm"><span className="font-mono text-xs text-brand">{String(i + 1).padStart(2, "0")}</span>{s}</li>)}</ul>
                </div>
                <div className="pt-3">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground"><span>Sikkerheit i tolkinga</span><span className="font-mono">{Math.round((ai.sikkerheit || 0) * 100)} %</span></div>
                  <div className="mt-1.5 h-1 w-full rounded-full bg-secondary"><div className="h-1 rounded-full bg-brand" style={{ width: `${Math.round((ai.sikkerheit || 0) * 100)}%` }} /></div>
                </div>
              </div>
            )}
          </Panel>

          <Panel title="Handsaming">
            <div className="space-y-3">
              <Field label="Status">
                <Select value={inq.status} onValueChange={(v) => patch({ status: v }, "Status oppdatert")}>
                  <SelectTrigger data-testid="inquiry-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(INQ_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <AssignedSelect inq={inq} onChange={(v) => patch({ assigned_to: v }, "Ansvarleg oppdatert")} />
            </div>
          </Panel>
        </div>
      </div>

      {convertOpen && <ConvertDialog key={inq.id + (inq.ai_at || "")} inq={inq} open={convertOpen} onOpenChange={setConvertOpen} onDone={(r) => { onChanged(); navigate(`/drift/oppdrag/${r.order.id}`); }} />}
    </div>
  );
}

function AssignedSelect({ inq, onChange }) {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/drift/users").then((r) => setUsers(r.data.filter((u) => u.role !== "montor"))).catch(() => {}); }, []);
  return (
    <Field label="Ansvarleg">
      <Select value={inq.assigned_to || inq.suggested_user_id || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
        <SelectTrigger data-testid="inquiry-assigned-select"><SelectValue placeholder="Ikkje tildelt" /></SelectTrigger>
        <SelectContent><SelectItem value={NONE}>Ikkje tildelt</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.id === inq.suggested_user_id ? " (foreslått)" : ""}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
}

export default function Inquiries() {
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const status = sp.get("status") || "ny";
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    return api.get(`/drift/inquiries?status=${status}${q ? `&q=${encodeURIComponent(q)}` : ""}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [status, q]);

  useEffect(() => { setLoading(true); const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const setStatus = (s) => setSp(s === "ny" ? {} : { status: s });

  return (
    <div data-testid="inquiries-page">
      <PageHeader eyebrow="Innboks" title="Henvendingar" subtitle="Førespurnader frå nettsida, strukturerte og klare til handsaming." />
      <div className="grid gap-5 lg:grid-cols-[360px_1fr] xl:grid-cols-[400px_1fr]">
        <div className={cn("lg:sticky lg:top-[88px] lg:h-[calc(100vh-7.5rem)]", id && "hidden lg:block")}>
          <InquiryList items={data.items} counts={data.counts} status={status} setStatus={setStatus} activeId={id} q={q} setQ={setQ} loading={loading} />
        </div>
        <div className="min-w-0">
          {id ? <InquiryDetail id={id} onChanged={load} /> : (
            <div className="hidden lg:block"><EmptyState title="Vel ei henvending" text="Klikk på ei henvending i lista for å sjå detaljar og forslag." testid="inquiries-empty-detail" /></div>
          )}
        </div>
      </div>
    </div>
  );
}
